import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import type { Auction } from '@otw/models'

const AUCTIONS_COLLECTION = 'auctions'

export type CreateAuctionInput = {
  name: string
  gate_passes_needed: boolean
}

interface UseAuctionsReturn {
  auctions: Auction[] | undefined
  isLoading: boolean
  error: Error | null
  createAuction: (input: CreateAuctionInput) => Promise<Auction>
  updateAuction: (auction: Auction) => Promise<Auction>
  deleteAuction: (id: string) => Promise<string>
}

const stripUndefinedFields = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as Partial<T>

const mapAuctionDoc = (snapshot: QueryDocumentSnapshot<DocumentData>): Auction => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    name: typeof data.name === 'string' ? data.name : '',
    gate_passes_needed: data.gate_passes_needed !== false,
    created_at: data.created_at ?? null,
    created_by: data.created_by ?? null,
    updated_at: data.updated_at ?? null,
  }
}

export const useAuctions = (): UseAuctionsReturn => {
  const { showSuccess, showError } = useSnackbar()
  const { userInfo } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = ['auctions'] as const

  const {
    data: auctions,
    isLoading,
    error,
  } = useQuery<Auction[]>({
    queryKey,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, AUCTIONS_COLLECTION))
      return snapshot.docs
        .map(mapAuctionDoc)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    },
    staleTime: 1000 * 60 * 5,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateAuctionInput) => {
      const name = input.name.trim()
      if (!name) throw new Error('Auction name is required')
      const payload = stripUndefinedFields({
        name,
        gate_passes_needed: Boolean(input.gate_passes_needed),
        created_by: userInfo?.id ? String(userInfo.id) : null,
      })
      const docRef = await addDoc(collection(db, AUCTIONS_COLLECTION), {
        ...payload,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      })
      return {
        id: docRef.id,
        name,
        gate_passes_needed: Boolean(input.gate_passes_needed),
        created_by: userInfo?.id ? String(userInfo.id) : null,
        created_at: new Date(),
        updated_at: new Date(),
      } satisfies Auction
    },
    onMutate: async (newAuction) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Auction[]>(queryKey)
      const tempId = `temp-${Date.now()}`
      const optimistic: Auction = {
        id: tempId,
        name: newAuction.name.trim(),
        gate_passes_needed: Boolean(newAuction.gate_passes_needed),
        created_by: userInfo?.id ? String(userInfo.id) : null,
        created_at: new Date(),
        updated_at: new Date(),
      }
      queryClient.setQueryData<Auction[]>(queryKey, (old) =>
        [...(old || []), optimistic].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
      )
      return { previous, tempId }
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      showError(`Failed to create auction: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: (result, _variables, context) => {
      if (context?.tempId) {
        queryClient.setQueryData<Auction[]>(queryKey, (old) => {
          if (!old) return [result]
          return old
            .map((item) => (item.id === context.tempId ? result : item))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        })
      }
      showSuccess('Auction created successfully')
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (auction: Auction) => {
      if (!auction.id) throw new Error('Auction ID is required')
      const name = auction.name.trim()
      if (!name) throw new Error('Auction name is required')
      const { id, created_at: _createdAt, created_by: _createdBy, ...rest } = auction
      const sanitized = stripUndefinedFields({
        ...rest,
        name,
        gate_passes_needed: Boolean(auction.gate_passes_needed),
      } as Record<string, unknown>)
      await updateDoc(doc(db, AUCTIONS_COLLECTION, id), {
        ...sanitized,
        updated_at: serverTimestamp(),
      })
      return { ...auction, name, gate_passes_needed: Boolean(auction.gate_passes_needed) }
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Auction[]>(queryKey)
      queryClient.setQueryData<Auction[]>(queryKey, (old) => {
        if (!old) return old
        return old
          .map((item) =>
            item.id === updated.id ? { ...item, ...updated, updated_at: new Date() } : item,
          )
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      })
      return { previous }
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      showError(`Failed to update auction: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Auction updated successfully')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, AUCTIONS_COLLECTION, id))
      return id
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Auction[]>(queryKey)
      queryClient.setQueryData<Auction[]>(queryKey, (old) => old?.filter((item) => item.id !== id))
      return { previous }
    },
    onError: (err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      showError(`Failed to delete auction: ${err instanceof Error ? err.message : 'Unknown error'}`)
    },
    onSuccess: () => {
      showSuccess('Auction deleted successfully')
    },
  })

  return {
    auctions,
    isLoading,
    error: error as Error | null,
    createAuction: (input) => createMutation.mutateAsync(input),
    updateAuction: (auction) => updateMutation.mutateAsync(auction),
    deleteAuction: (id) => deleteMutation.mutateAsync(id),
  }
}
